import React from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  onNavigate?: (item: BreadcrumbItem, index: number) => void;
  className?: string;
  theme?: 'light' | 'dark';
  backOnFirst?: boolean; // If true, clicking the first breadcrumb goes back in history by default
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  onNavigate,
  className = '',
  theme = 'light',
  backOnFirst = true,
}) => {
  if (!items.length) return null;

  const handleClick = (item: BreadcrumbItem, index: number) => {
    // Default behavior: first item acts as a back button
    if (index === 0 && backOnFirst) {
      if (window.history.length > 1) {
        window.history.back();
      } else if (item.href) {
        // Safe fallback if no history
        window.location.href = item.href;
      }
      return;
    }
    if (onNavigate) {
      onNavigate(item, index);
    }
  };

  // Define theme-based styles
  const styles = {
    active: theme === 'light' 
      ? 'text-blue-600 hover:text-blue-800' 
      : 'text-white hover:text-blue-200',
    inactive: theme === 'light' 
      ? 'text-gray-500' 
      : 'text-white/50',
    separator: theme === 'light' 
      ? 'text-gray-500' 
      : 'text-white/50',
    backButton: theme === 'light'
      ? 'bg-gray-900 group-hover:bg-gray-700'
      : 'bg-gray-900 group-hover:bg-gray-700'
  };

  return (
    <div className={`flex items-center text-sm ${className}`}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index === 0 && (
            <div
              className="flex items-center gap-2 cursor-pointer group"
              onClick={() => handleClick(item, index)}
            >
              {item.icon ? (
                item.icon
              ) : (
                <div className={`rounded-full p-1 transition-colors ${styles.backButton}`}>
                  <ArrowLeftIcon className="w-3 h-3 text-white stroke-2" />
                </div>
              )}
              <span className={`transition-colors ${styles.active}`}>
                {item.label}
              </span>
            </div>
          )}
          {index > 0 && (
            <>
              <span className={`mx-2 ${styles.separator}`}>›</span>
              {index === items.length - 1 ? (
                <span className={styles.inactive}>{item.label}</span>
              ) : (
                <span
                  className={`cursor-pointer transition-colors ${styles.active}`}
                  onClick={() => handleClick(item, index)}
                >
                  {item.label}
                </span>
              )}
            </>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default Breadcrumb; 